import React, { SFC, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Dialog,
    DialogTitle,
    makeStyles,
    DialogContent,
    Grid,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    DialogActions,
} from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { Location } from '../../../shared/types/location'
import { fetchLocationPricingConditions } from '../../../redux/actions/locations'
import { PricingConditionModel } from '../../../shared/types/pricingCondition'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { ChargerConnectorType } from '../../../shared/types/charger'
import { Day, TariffRowModel } from '../../../shared/types/tariff'
import { fetchTags } from '../../../redux/actions/tags'
import { fetchTariffs } from '../../../redux/actions/tariffs'

const useStyles = makeStyles((theme) => ({
    dialog: {
        // '& .MuiPaper-root': {
        //     width: '600px',
        //     maxWidth: '100%',
        // },
        '& .MuiFilledInput-root': {
            borderRadius: theme.spacing(0.5),
        },
        '& .MuiFilledInput-root::before': {
            border: 0,
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0,
        },
        '& input[type=number]': {
            '-moz-appearance': 'textfield',
        },
        '& .DraftEditor-editorContainer': {
            maxHeight: 'calc(100vh - 272px)',
        },
    },
    fullWidth: {
        width: '100%',
    },
    dialogActions: {
        padding: theme.spacing(1, 3, 3, 3),
    },
    profileItem: {
        textAlign: 'left',
    },
    profileValue: {
        paddingLeft: theme.spacing(1),
        textAlign: 'left',
        position: 'relative',
        width: '80%',
        '& span': {
            position: 'absolute',
            top: theme.spacing(0.3),
        },
    },
    accordionTitle: {
        width: '302px',
    },
    sectionHeader: {
        marginBottom: '10px',
    },
    scheduleNote: {
        marginBottom: '10px',
    },
}))

interface LocationAddEditDialogProps {
    open: boolean
    onClose(): void
    initialModel?: Location
}

const LocationPricingConditionsDialog: SFC<LocationAddEditDialogProps> = ({ open, onClose, initialModel }) => {
    const classes = useStyles()
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const pricingConditions = useSelector((state: any) => state.locations?.locationPricingConditions?.value)
    const tags = useSelector((state: any) => state.tags.items.all.value)
    const tariffs = useSelector((state: any) => state.tariffs.items.value)
    const tenantId = useSelector((state: any) => state.login.value.user.tenantId)

    useEffect(() => {
        if (initialModel?.id) {
            dispatch(fetchLocationPricingConditions(initialModel.id))
        }
    }, [open, initialModel, dispatch])

    useEffect(() => {
        dispatch(fetchTags())
        dispatch(fetchTariffs(tenantId))
    }, [dispatch, tenantId])

    const onDialogClose = () => {
        onClose()
    }

    const formatDate = (dateString: Date) => {
        const date = new Date(dateString)

        const day = date.getDate()
        const month = date.toLocaleString('en-GB', { month: 'long' }) // Full month name
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')

        return `${day}. ${month} ${year}, ${hours}:${minutes}`
    }

    const getChargingUnits = (connectorType: ChargerConnectorType | null | undefined) => {
        switch (connectorType) {
            case ChargerConnectorType.Ac:
                return ' A'
            case ChargerConnectorType.Dc:
                return ' kW'
            default:
                return ''
        }
    }

    const getConnectorTypeName = (type: ChargerConnectorType | null | undefined): string => {
        switch (type) {
            case ChargerConnectorType.Ac:
                return 'AC'
            case ChargerConnectorType.Dc:
                return 'DC'
            default:
                return ''
        }
    }

    return (
        <Dialog className={classes.dialog} open={open}>
            <DialogTitle>{t('pricingConditions.title')}</DialogTitle>
            <DialogContent>
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12}>
                        {Array.isArray(pricingConditions) && pricingConditions.length > 0 ? (
                            <div>
                                {pricingConditions.map((condition: PricingConditionModel) => {
                                    // Create pricingConditionValues for the current condition
                                    const pricingConditionValues = [
                                        [
                                            t('pricingConditions.enableFlexiCharging'),
                                            !condition.enableFlexiCharging ? 'No' : 'Yes',
                                        ],
                                        [
                                            t('pricingConditions.maxSessionsPerUser'),
                                            !condition.maxSessionsPerUser || condition.maxSessionsPerUser === '0'
                                                ? 'Unrestricted'
                                                : condition.maxSessionsPerUser,
                                        ],
                                        [
                                            t('pricingConditions.connectorType'),
                                            getConnectorTypeName(condition.connectorType),
                                        ],
                                        [t('pricingConditions.country'), condition.country],
                                        [t('pricingConditions.validFrom'), formatDate(condition.validFrom)],
                                        [t('pricingConditions.validTo'), formatDate(condition.validTo)],
                                        [
                                            t('pricingConditions.priceForKwh'),
                                            !condition.priceForKwh || condition.priceForKwh === '0'
                                                ? 'Unrestricted'
                                                : condition.priceForKwh + ' €',
                                        ],
                                        [
                                            t('pricingConditions.limitPerSession'),
                                            !condition.limitPerSession || condition.limitPerSession === '0'
                                                ? 'Unrestricted'
                                                : condition.limitPerSession + ' kWh',
                                        ],
                                        [
                                            t('pricingConditions.priceForMinuteOfParking'),
                                            !condition.priceForMinuteOfParking ||
                                            condition.priceForMinuteOfParking === '0'
                                                ? 'Unrestricted'
                                                : condition.priceForMinuteOfParking + ' €',
                                        ],
                                        [
                                            t('pricingConditions.amountOfFreeParking'),
                                            !condition.amountOfFreeParking || condition.amountOfFreeParking === '0'
                                                ? 'Unrestricted'
                                                : condition.amountOfFreeParking + ' min',
                                        ],
                                        [
                                            t('pricingConditions.maxSpeedOfCharging'),
                                            !condition.maxSpeedOfCharging || condition.maxSpeedOfCharging === '0'
                                                ? 'Unrestricted'
                                                : condition.maxSpeedOfCharging +
                                                  getChargingUnits(condition.connectorType),
                                        ],
                                        [
                                            t('pricingConditions.maximumDurationOfCharging'),
                                            !condition.maximumDurationOfCharging ||
                                            condition.maximumDurationOfCharging === '0'
                                                ? 'Unrestricted'
                                                : condition.maximumDurationOfCharging + ' min',
                                        ],
                                        [
                                            t('pricingConditions.userTag'),
                                            tags?.find((tag: any) => tag.id === condition.userTag)?.name,
                                        ],
                                        [
                                            t('pricingConditions.chargerTag'),
                                            tags?.find((tag: any) => tag.id === condition.chargerTag)?.name,
                                        ],
                                        [
                                            t('pricingConditions.locationTag'),
                                            tags?.find((tag: any) => tag.id === condition.locationTag)?.name,
                                        ],
                                        [
                                            t('pricingConditions.chargingProfileTag'),
                                            tags?.find((tag: any) => tag.id === condition.chargingProfileTag)?.name,
                                        ],
                                    ]

                                    return (
                                        <Accordion key={condition.id}>
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon />}
                                                aria-controls="panel1a-content"
                                                id={`panel-${condition.id}-header`}
                                            >
                                                <Grid item className={classes.accordionTitle}>
                                                    <Typography variant="body1">{condition.name}</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="caption">
                                                        {' '}
                                                        {t('pricingConditions.priority')}: {condition.priority}{' '}
                                                    </Typography>
                                                </Grid>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                {/* Two-column layout for the rest of the information */}
                                                <Grid container spacing={2}>
                                                    {condition.description && (
                                                        <Grid item xs={12}>
                                                            <Typography variant="body2">
                                                                {condition.description}
                                                            </Typography>
                                                        </Grid>
                                                    )}
                                                    <Grid item xs={12}>
                                                        {pricingConditionValues.map((x: any, index) =>
                                                            x.length === 2 && x[1] ? (
                                                                <Grid container key={index}>
                                                                    <Grid item xs={7} className={classes.profileItem}>
                                                                        <Typography variant="body2">{x[0]}</Typography>
                                                                    </Grid>
                                                                    <Grid item xs={5} className={classes.profileValue}>
                                                                        <Typography variant="caption" component="div">
                                                                            {x[1]}
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            ) : null
                                                        )}
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        {condition.scheduleTariff &&
                                                            (() => {
                                                                const selectedTariff = tariffs?.find(
                                                                    (tariff: any) =>
                                                                        tariff.id === condition.scheduleTariff
                                                                )
                                                                return selectedTariff ? (
                                                                    <div>
                                                                        <Typography
                                                                            variant="body1"
                                                                            className={classes.sectionHeader}
                                                                        >
                                                                            {t('common.name')}: {selectedTariff.name}
                                                                        </Typography>
                                                                        {selectedTariff.note && (
                                                                            <Grid
                                                                                item
                                                                                xs={12}
                                                                                className={classes.scheduleNote}
                                                                            >
                                                                                <Typography variant="body2">
                                                                                    {selectedTariff.note}
                                                                                </Typography>
                                                                            </Grid>
                                                                        )}
                                                                        {selectedTariff.rows.map(
                                                                            (row: TariffRowModel, index: number) => (
                                                                                <Grid container key={index} spacing={1}>
                                                                                    <Grid item xs={12}>
                                                                                        <Typography variant="body2">
                                                                                            {Day[row.dayFrom]}{' '}
                                                                                            {row.from} -{' '}
                                                                                            {Day[row.dayTo]} {row.to}
                                                                                        </Typography>
                                                                                    </Grid>
                                                                                </Grid>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                ) : null
                                                            })()}
                                                    </Grid>
                                                </Grid>
                                            </AccordionDetails>
                                        </Accordion>
                                    )
                                })}
                            </div>
                        ) : (
                            <p>{t('pricingConditions.noPricingConditions')}</p> // Add a translation key for this message
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button onClick={onDialogClose} color="primary">
                    {t('common.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default LocationPricingConditionsDialog
